import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PagedResponse } from '../core';
import {
  CreateDiscountRequest,
  DiscountApiService,
  ExpireDiscountRequest,
  ListDiscountsRequest,
  PagedRequestOptions,
  UpdateDiscountRequest,
} from '@vendasta/billing';
import { Discount, discountFromApi } from './discount';
import { DiscountType, discountTypeToApi } from './discount_type';
import { ListDiscountsFilter } from './list-discount';
import { DiscountConsumer, discountConsumerToApi } from './discount_consumer';

@Injectable({ providedIn: 'root' })
export class DiscountService {
  constructor(private discountApi: DiscountApiService) {}

  list(filters: ListDiscountsFilter, cursor: string, pageSize: number): Observable<PagedResponse<Discount>> {
    const req = new ListDiscountsRequest({
      filters: filters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.discountApi.list(req).pipe(
      map((response) => {
        const discounts = (response.discounts || []).map((item) => discountFromApi(item));
        return new PagedResponse(discounts, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  create(
    merchantId: string,
    sku: string,
    discountType: DiscountType,
    amount: number,
    startDateTime: string,
    endDateTime?: string,
    resetEachPeriodFlag?: boolean,
    description?: string,
    customerId?: string,
    discountConsumer = DiscountConsumer.WHOLESALE,
    subscriptionId?: string,
    numberOfBillingPeriods?: number,
  ): Observable<string> {
    const start = new Date(startDateTime);
    const dt = discountTypeToApi(discountType);
    const consumer = discountConsumerToApi(discountConsumer);

    const r = new CreateDiscountRequest({
      merchantId: merchantId,
      sku: sku,
      discountType: dt,
      amount: amount,
      start: start,
      resetEachPeriod: resetEachPeriodFlag,
      description: description,
      customerId: customerId,
      consumer: consumer,
      subscriptionId: subscriptionId,
      numberOfBillingPeriods: numberOfBillingPeriods ?? 0,
    });

    if (endDateTime && endDateTime !== '') {
      r.end = new Date(endDateTime);
    }

    return this.discountApi.create(r).pipe(map((resp) => resp.id));
  }

  update(
    merchantId: string,
    id: string,
    discountType: DiscountType,
    amount: number,
    startDateTime: string,
    endDateTime?: string,
    resetEachPeriodFlag?: boolean,
    description?: string,
    customerId?: string,
    subscriptionId?: string,
    numberOfBillingPeriods?: number,
  ): Observable<HttpResponse<null>> {
    const start = new Date(startDateTime);
    const dt = discountTypeToApi(discountType);

    const r = new UpdateDiscountRequest({
      merchantId: merchantId,
      id: id,
      discountType: dt,
      amount: amount,
      start: start,
      resetEachPeriod: resetEachPeriodFlag,
      description: description,
      customerId: customerId,
      subscriptionId: subscriptionId,
      numberOfBillingPeriods: numberOfBillingPeriods ?? 0,
    });

    if (endDateTime && endDateTime !== '') {
      r.end = new Date(endDateTime);
    }
    return this.discountApi.update(r);
  }

  expire(merchantId: string, id: string): Observable<HttpResponse<null>> {
    return this.discountApi.expire(
      new ExpireDiscountRequest({
        merchantId: merchantId,
        id: id,
      }),
    );
  }
}
